import { FormInstance } from 'antd-mobile/es/components/form'
import { pick } from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { IServiceProviderVO, IProviderFormValues, IExpressProps, IProviderAddressVO, ISetProviderProps, ExpressListData, IExpressData } from './types'
import {
  getShipmentServiceType,
  getCodFee,
  handlePayment,
  initialPickupTimeWithChange,
  handleSubAccount,
  getServiceProvider,
  logger,
  getDeliveryTime,
  getExpressConfig,
  sortProviderList,
} from './utils'
import { isKerryHk2TWFn } from '@/utils/form/serviceProvider'
import type { AppThunk, AppDispatch, AppGetState, AppThunkDispatch } from '@/store'
import { CUSTOM_FREIGHT_REASON_ENUM, PaymentTypeEnum, SHIPMENT_PICKUP_TYPE } from '@/store/serviceProviderSlice/enum'
import { Toast } from 'antd-mobile'
import { divide, times } from 'number-precision'
import i18n from '@/i18n'
import { postFeeConvert } from '@/services/api'

import { ENUM_TYPES, COD_TAB } from './enum'
import { isCodCurrencyCodeSpecified, formatSenderAddressToService, formatRecipientAddressToService } from '@/utils'
import { DELIVER_VALUE_MAP, getParcelInfo, handleCalculateFreightAndFeeItems } from './utils/feeItem'

const initialState: Partial<IServiceProviderVO> = {
  type: 'normal',
  tab: `${COD_TAB.ALL}`,
  filterArr: [],
  popupVisible: false,
}

const configSlice = createSlice({
  name: 'serviceProvider',
  initialState,
  reducers: {
    setProviderOptions(state, action: PayloadAction<IServiceProviderVO>) {
      return {
        ...state,
        ...action.payload,
      }
    },
    update(state, action: PayloadAction<Partial<IServiceProviderVO>>) {
      return {
        ...state,
        ...action.payload,
      }
    },
    /**
     * 更新依赖的 query
     * @param state
     * @param action
     * @returns
     */
    updatePropsAction(state, action: PayloadAction<IExpressProps>) {
      const props = action.payload
      return {
        ...state,
        props,
      }
    },
  },
})

export const { setProviderOptions, update, updatePropsAction } = configSlice.actions

export const setProps = (props: ISetProviderProps) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { serviceProvider: state, dictionary } = getState()
  const { cover, type, ...payload } = props

  // 覆盖
  if (cover) {
    return {
      ...state,
      props: payload,
    }
  }

  const addressVO = {
    ...(state.props?.addressVO || {}),
    ...(payload?.addressVO || {}),
  }

  // update
  const updateProps = {
    ...(state.props || {}),
    ...payload,
    addressVO,
  }

  // 兼容字段
  if (props.arrival_method) {
    updateProps.supportedAddressTypes = [props.arrival_method == 1 ? 'HOME_STREET' : 'STORE_STREET']
  }

  // 第一次初始化，可能没有 currencyCode
  if (props?.addressVO?.arrival_countryCode && !state?.props?.currencyCode && !props.currencyCode) {
    const { arrival_countryCode: arrivalCountry } = props?.addressVO
    const arrivalCurrencyCode = dictionary?.countryMapDics?.[arrivalCountry]?.currency
    if (arrivalCurrencyCode) {
      updateProps.currencyCode = arrivalCurrencyCode
    }
  }

  if (type) {
    await dispatch(
      update({
        type,
      })
    )
  }

  await dispatch(updatePropsAction(updateProps))

  /** 修改了收货国家 */
  if (payload?.addressVO?.arrival_countryCode) {
    dispatch(updateShippingInfo())
  }

  if (props.previousShipmentServiceType) {
    dispatch(
      update({
        previousShipmentServiceType: props.previousShipmentServiceType,
      })
    )
  }
}

type IFetchProviderList = {
  setStore?: boolean
  curExpTypeCode?: string
  orderCodCurrencyCode?: string
  form?: FormInstance
}

const defaultFetchParameters = {
  setStore: true,
}

export const fetchProviderList = (parameters?: IFetchProviderList) => async (dispatch, getState: AppGetState, form: FormInstance) => {
  const { setStore, curExpTypeCode, orderCodCurrencyCode, form: inputForm } = Object.assign({}, defaultFetchParameters, parameters)

  const state = getState()
  const { auth, serviceProvider } = state
  const { isPrepaid } = auth?.userInfo ?? {}
  const currencyCode = auth?.userInfo?.currencyCode
  const departure_countryCode = form.getFieldValue('departure_countryCode')
  const currentForm = inputForm || form

  setStore &&
    dispatch(
      update({
        listLoading: true,
      })
    )
  try {
    const list = await getServiceProvider({
      currencyCode,
      isPrepaid,
      props: {
        ...serviceProvider.props,
        addressVO: {
          ...serviceProvider?.props.addressVO,
          departure_countryCode,
        },
        curExpTypeCode: curExpTypeCode || serviceProvider?.provider?.expressTypeCode,
      },
      type: serviceProvider.type,
      orderCodCurrencyCode,
      provider_pickup_type: (inputForm || form).getFieldValue('provider_pickup_type'),
      partitionInfo: {
        ...getParcelInfo(currentForm),
      },
    })
    setStore &&
      dispatch(
        update({
          list,
        })
      )
    return list
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('get service provider list error', error)
    return []
  } finally {
    setStore &&
      dispatch(
        update({
          listLoading: false,
        })
      )
  }
}

export interface HandleOptionsData {
  provider: Partial<ExpressListData>
  formValues?: IProviderFormValues
}

export const handleExtraConfig = (data: HandleOptionsData) => async (_: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { serviceProvider } = getState()
  const { props, type } = serviceProvider
  const { provider, formValues } = data
  const departure_countryCode = form?.getFieldValue('departure_countryCode')

  /**
   * 获取揽收时间和税号展示配置
   * pickupTime 是跟服务产品具体指挂钩的，因此这里需要考虑数据默认值跟服务产品默认值的情况，因此他的初始化Options是根据具体值来确定的，而不是无脑取服务产品的值
   */
  const config = await (async () => {
    const { supportedPickupTypes, expressTypeCode, priceSettingInfo } = provider

    const expressConfig = await getExpressConfig({
      formValues,
      supportedPickupTypes,
      expressTypeCode,
      address: {
        senderCountryCode: departure_countryCode || props.addressVO?.departure_countryCode,
        consigneeCountryCode: props.addressVO?.arrival_countryCode,
      },
      priceSettingInfo,
      type,
    })

    // 默认选中的税号类型
    const defaultTaxType = formValues?.provider_tax_type || expressConfig?.extraConfig?.find((item) => item.key === 'customerTax')?.options?.[0]?.option

    return { ...expressConfig, defaultTaxType }
  })()

  return config
}

/**
 * 处理服务产品的 options
 * @description
 * 这里provider的来源可能是默认的服务产品信息，也有可能是用户在服务产品列表选择的信息
 */
export const handleOptions = (data: HandleOptionsData & { isInit?: boolean }) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { provider = {}, formValues, isInit } = data
  const state = getState()
  const {
    serviceProvider: { type },
    auth: { walletList },
  } = state
  const departure_countryCode = form.getFieldValue('departure_countryCode')
  const { companyCode, settlementModes, estimatedCost, freight, expressTypeCode, needSubAccount, saasFlag, supportedCheckoutTypes, freightCurrencyCode } = provider

  /** cod 选项 */
  const provider_payment_type = (() => {
    if (formValues?.provider_payment_type) {
      return formValues.provider_payment_type
    }

    const { type, filterArr, previousShipmentServiceType } = state.serviceProvider
    const provider_payment_type = getShipmentServiceType({
      supportedCheckoutTypes,
      type,
      filterArr,
      previousShipmentServiceType,
    })

    return provider_payment_type
  })()

  /** 货到付款金额 */
  const provider_cod_amount = (() => {
    if (formValues?.provider_cod_amount) {
      return formValues.provider_cod_amount
    }

    const { type, previousCodFee, props } = state.serviceProvider
    const orderCodAmount = props?.orderInfo?.codAmount
    const provider_cod_amount = getCodFee({
      provider_payment_type: provider_payment_type && Number(provider_payment_type),
      type,
      previousCodFee,
      orderCodAmount,
    })

    return provider_cod_amount
  })()

  /**
   * 处理支付方式
   */
  const paymentData = (() => {
    const isPrepaid = state.auth?.userInfo?.isPrepaid
    const userCurrencyCodeList = walletList.map((wallet) => wallet.currencyCode)
    const freightCurrencyCodeNoInUserCurrencyCodeList = freightCurrencyCode && !userCurrencyCodeList.includes(freightCurrencyCode)

    const paymentData = handlePayment({
      companyCode,
      settlementModes,
      estimatedCost: estimatedCost || freight,
      isPrepaid,
      saasFlag,
      type,
      freightCurrencyCodeNoInUserCurrencyCodeList,
    })
    return paymentData
  })()

  /**
   * 获取揽收时间和税号展示配置
   * pickupTime 是跟服务产品具体指挂钩的，因此这里需要考虑数据默认值跟服务产品默认值的情况，因此他的初始化Options是根据具体值来确定的，而不是无脑取服务产品的值
   */
  const { dateData, extraConfig, defaultTaxType, supportStationPickUp } = await dispatch(
    handleExtraConfig({
      provider,
      formValues,
    })
  )

  const isKerryHk2TW = (() => {
    const { addressVO } = state.serviceProvider.props ?? {}
    if (addressVO && departure_countryCode && addressVO.arrival_countryCode) {
      return isKerryHk2TWFn(companyCode, departure_countryCode, addressVO.arrival_countryCode)
    } else {
      logger('发货国家或收货国家没有初始化')
      return false
    }
  })()

  const subAccountData = await (async () => {
    if (needSubAccount) {
      const { list, value } = await handleSubAccount({
        expressTypeCode,
        pickupType: companyCode === 'UPS' ? 0 : formValues?.provider_pickup_type ?? dateData.provider_pickup_type,
      })

      // 揽收方式为dropoff 到店寄件 时子账号返回空,toast配置账号
      if (!isInit && !list.length && dateData.provider_pickup_type === SHIPMENT_PICKUP_TYPE.dropOff) {
        Toast.show({
          icon: 'error',
          content: i18n.t('system.error.LST-123677-B0012'),
        })
      }

      return {
        list,
        defaultValue: value,
      }
    }

    return {
      list: [],
      defaultValue: undefined,
    }
  })()

  /** 处理预约派送时间 */
  const deliveryTimeData = getDeliveryTime({
    provider,
    formValues: pick(formValues, ['provider_delivery_dates']),
  })

  return {
    subAccountData,
    isKerryHk2TW,
    dateData,
    paymentData,
    provider_payment_type,
    provider_cod_amount,
    deliveryTimeData,
    extraConfig,
    defaultTaxType,
    supportStationPickUp,
  }
}

export const handlePickupMethodChangeWithSubAccount =
  (pickupType: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const { needSubAccount, expressTypeCode, companyCode } = state.serviceProvider.provider ?? {}
    if (!needSubAccount) return

    const { list, value } = await handleSubAccount({
      expressTypeCode,
      // ups使用通用子账号
      pickupType: companyCode === 'UPS' ? 0 : pickupType,
    })

    dispatch(
      update({
        subAccountOption: list.map((item) => ({ value: item.accountNo, label: item.remark })),
      })
    )
    form?.setFieldsValue({
      provider_channel_seller_id: value,
    })
  }

/**
 * 处理选中服务产品
 * @returns
 */
export const handleSelectServiceProviderSlice =
  (provider: ExpressListData, inputForm?: FormInstance) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
    const formInstance = inputForm ?? form
    await dispatch(calculateGoodsPrice(formInstance, provider))

    const state = getState()
    const { supportedCheckoutTypes } = provider
    const { provider: previousProvider, props } = state.serviceProvider

    /**
     * 处理 cod 选项
     */
    const provider_payment_type = (() => {
      const { type, filterArr, previousShipmentServiceType } = state.serviceProvider

      const provider_payment_type = getShipmentServiceType({
        supportedCheckoutTypes,
        type,
        filterArr,
        previousShipmentServiceType,
      })
      return provider_payment_type
    })()
    /**
     * 处理货到付款金额
     */
    let provider_cod_amount = (() => {
      const { type, previousCodFee, props } = getState().serviceProvider

      const orderCodAmount = props?.orderInfo?.codAmount
      const provider_cod_amount = getCodFee({
        provider_payment_type: provider_payment_type && Number(provider_payment_type),
        type,
        previousCodFee,
        orderCodAmount,
      })
      // 指定COD币种的服务产品，如果指定币种与当前COD币种不一致，COD币种会重置，金额也需要重置
      if (isCodCurrencyCodeSpecified(provider.codCurrencyType) && provider.targetCodCurrencyCode !== props.codCurrencyCode) {
        return ''
      }
      return provider_cod_amount
    })()

    let codCurrencyCode = props.codCurrencyCode

    // 处理 options
    const { dateData, paymentData, subAccountData, isKerryHk2TW, deliveryTimeData, extraConfig, defaultTaxType, supportStationPickUp } = await dispatch(
      handleOptions({
        provider,
      })
    )

    // set it frist, because the calculate feeItems needs this field value
    formInstance?.setFieldsValue({
      provider_pickup_type: dateData.provider_pickup_type,
    })

    // 切换到普通服务产品，如下两种情况，COD币种切换为商品币种，COD金额清空：
    const parcel_currency_code = formInstance.getFieldValue('parcel_currency_code')
    if (
      !isCodCurrencyCodeSpecified(provider.codCurrencyType) && // 1.上一个服务产品为指定币种服务产品，且指定的币种与商品币种不一致
      ((isCodCurrencyCodeSpecified(previousProvider?.codCurrencyType) && previousProvider.targetCodCurrencyCode !== parcel_currency_code) ||
        // 2.之前没有选择服务产品，且商品币种与订单原始COD币种不一致
        (!previousProvider && parcel_currency_code !== codCurrencyCode))
    ) {
      codCurrencyCode = parcel_currency_code
      provider_cod_amount = ''
    }

    dispatch(
      update({
        providerExtraConfig: extraConfig,
        providerDateOptions: dateData.shipmentPickupDateOptions,
        providerTimeOptions: dateData.shipmentPickupTimeRangOptions,
        supportStationPickUp,
        providerPaymentOptions: paymentData.options,
        subAccountOption: subAccountData.list?.map((item) => ({ value: item.accountNo, label: item.remark })),
        isKerryHk2TW,
        provider,
        previousShipmentServiceType: provider_payment_type,
        providerDeliveryDateOptions: deliveryTimeData.providerDeliveryDateOptions,
        providerDeliveryTimeOptions: deliveryTimeData.providerDeliveryTimeOptions,
        props: { ...state.serviceProvider.props, isAutoMatch: false, codCurrencyCode },
      })
    )

    formInstance?.setFieldsValue({
      provider_cod_amount,
      provider_payment_type,
      provider_pay_type: paymentData.defaultValue,
      provider_pickup_type: dateData.provider_pickup_type,
      provider_pickup_dates: dateData.provider_pickup_dates,
      provider_delivery_dates: deliveryTimeData.provider_delivery_dates,
      provider_channel_seller_id: subAccountData.defaultValue,
      provider_tax_type: defaultTaxType,
    })

    await clearCustomFreight(formInstance)
    await dispatch(handleCalculateCustomFreight(formInstance))
    await dispatch(handleCalculateFreightAndFeeItems(form))
  }

/**
 * 根据 expressCode 初始化服务产品
 * 不传 form 的话，可能拿到的 form 实例不是同一个
 * @param expressCode
 * @returns
 */
export const getProviderWithExpressCode =
  (expressCode: string) =>
  async (dispatch: AppThunkDispatch): Promise<{ provider?: ExpressListData; isSame: boolean }> => {
    const list = await dispatch(
      fetchProviderList({
        setStore: false,
        curExpTypeCode: expressCode,
      })
    )
    const newList = sortProviderList(list, { filter: true })

    const index = newList.findIndex((item) => item.expressTypeCode === expressCode)

    const provider = index !== -1 ? newList[index] : newList?.[0]

    return {
      provider,
      isSame: index !== -1,
    }
  }

/**
 * 从订单详情初始化
 * @param data
 * @returns
 */
export const initProviderAndForm =
  (payload: { provider: Partial<IExpressData>; formValues?: IProviderFormValues; props?: ISetProviderProps; form?: FormInstance; type?: `${ENUM_TYPES}` }) =>
  async (dispatch: AppThunkDispatch, getState, form: FormInstance) => {
    const { provider, formValues, props, form: inputForm, type } = payload

    if (type) {
      await dispatch(update({ type }))
    }
    // 先 set props
    if (props) {
      await dispatch(setProps(props))
    }
    // 如果 Provider 为空，不初始化后面
    if (!provider || !provider.companyCode || !provider?.expressTypeCode) {
      return
    }
    // 处理 options
    const { dateData, paymentData, subAccountData, isKerryHk2TW, provider_cod_amount, provider_payment_type, deliveryTimeData, extraConfig, defaultTaxType, supportStationPickUp } =
      await dispatch(
        handleOptions({
          provider,
          formValues,
          isInit: true,
        })
      )
    await dispatch(
      update({
        providerExtraConfig: extraConfig,
        providerDateOptions: dateData.shipmentPickupDateOptions,
        providerTimeOptions: dateData.shipmentPickupTimeRangOptions,
        supportStationPickUp,
        providerPaymentOptions: paymentData.options,
        subAccountOption: subAccountData.list?.map((item) => ({ value: item.accountNo, label: item.remark })),
        isKerryHk2TW,
        previousCodFee: formValues?.provider_cod_amount || undefined,
        previousShipmentServiceType: formValues?.provider_payment_type || undefined,
        provider,
        providerDeliveryDateOptions: deliveryTimeData.providerDeliveryDateOptions,
        providerDeliveryTimeOptions: deliveryTimeData.providerDeliveryTimeOptions,
      })
    )

    const updateFormValues: Record<string, any> = {
      provider_pay_type: paymentData.defaultValue,
      provider_pickup_type: dateData.provider_pickup_type,
      provider_pickup_dates: dateData.provider_pickup_dates,
      provider_channel_seller_id: subAccountData.defaultValue,
      provider_cod_amount,
      provider_payment_type,
      provider_tax_type: defaultTaxType,
    }
    // 如果有传入值，优先使用 form 的值
    if (formValues) {
      Object.assign(updateFormValues, {
        ...formValues,
        provider_invoice_date: formValues?.provider_invoice_date ? new Date(formValues?.provider_invoice_date) : undefined,
        provider_channel_seller_id: formValues.provider_channel_seller_id || subAccountData.defaultValue,
        provider_pickup_type: formValues.provider_pickup_type ?? dateData.provider_pickup_type,
      })
    }
    Object.assign(updateFormValues, {
      provider_pay_type: paymentData.list.includes(updateFormValues.provider_pay_type) ? updateFormValues.provider_pay_type : paymentData.defaultValue,
    })

    Object.assign(updateFormValues, {
      provider_delivery_dates: deliveryTimeData.provider_delivery_dates,
    })
    // 指定COD币种的服务产品，COD币种会重置，金额也重置
    if (isCodCurrencyCodeSpecified(provider?.codCurrencyType) && provider?.targetCodCurrencyCode !== props.codCurrencyCode) {
      Object.assign(updateFormValues, {
        provider_cod_amount: '',
      })
    }

    const finalFormValues = pick(
      updateFormValues,
      Object.keys(updateFormValues).filter((key) => key.startsWith('provider_'))
    )

    // 部分签收
    if (typeof updateFormValues.provider_support_part_sign === 'string') {
      finalFormValues.provider_support_part_sign = updateFormValues.provider_support_part_sign
    }
    ;(inputForm || form)?.setFieldsValue(finalFormValues)
    // after set the provider_cod_amount it needs to update freight and feeItems
    await dispatch(handleCalculateFreightAndFeeItems(form))
  }

export const initProviderAndPropsWithExpressCode =
  (payload: { formValues?: IProviderFormValues; props?: IExpressProps; expressTypeCode: string }) => async (dispatch: AppThunkDispatch, getState, form: FormInstance) => {
    const { formValues, expressTypeCode, props } = payload

    if (expressTypeCode) {
      await dispatch(setProps(props))
      const { provider, isSame } = await dispatch(getProviderWithExpressCode(expressTypeCode))

      isSame &&
        (await dispatch(
          initProviderAndForm({
            provider,
            props,
            formValues,
            form,
          })
        ))
    }
  }

/**
 * 修改揽收方式
 * @param provider_pickup_type
 * @returns
 */
export const handleShipmentPickupTypeChange =
  (provider_pickup_type: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const providerDateOptions = state.serviceProvider.providerDateOptions
    const { providerTimeOptions, ...formValues } = initialPickupTimeWithChange(provider_pickup_type, {
      providerDateOptions,
    })

    dispatch(
      update({
        providerTimeOptions,
      })
    )

    form.setFieldsValue(formValues)
    dispatch(handleShipmentDateChange(formValues.provider_pickup_date))

    dispatch(handleCalculateFreightAndFeeItems(form))
  }

/**
 * 监听揽收日期
 * @param provider_pickup_date
 * @returns
 */
export const handleShipmentDateChange =
  (provider_pickup_date: string): AppThunk =>
  (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const provider_pickup_dates = [provider_pickup_date]
    const timeOptions = state.serviceProvider.providerDateOptions.find((item) => item.date === provider_pickup_date)?.timeIntervals ?? []

    if (timeOptions?.[0]) {
      provider_pickup_dates.push(timeOptions?.[0])
    }

    dispatch(
      update({
        providerTimeOptions: timeOptions,
      })
    )

    form?.setFieldsValue({
      provider_pickup_dates,
    })
  }

export const handleShipmentServiceType =
  (provider_payment_type: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    if (provider_payment_type === 1) {
      const { serviceProvider } = getState()
      const { previousCodFee } = serviceProvider
      form.setFieldsValue({
        provider_cod_amount: previousCodFee,
      })
      await dispatch(handleCalculateCustomFreight(form, previousCodFee as number))
    }

    dispatch(
      update({
        previousShipmentServiceType: provider_payment_type,
      })
    )

    dispatch(handleCalculateFreightAndFeeItems(form))
  }

export const handleKeepPreviousCodFee =
  (codFee): AppThunk =>
  (dispatch) => {
    dispatch(
      update({
        previousCodFee: codFee,
      })
    )
  }

/**
 * 更新地址数据
 * @param payload
 * @returns
 */
export const updateAddressVO = (payload: IProviderAddressVO) => (dispatch, getState: AppGetState) => {
  const { serviceProvider } = getState()
  const { props } = serviceProvider

  const addressVO = {
    ...(props?.addressVO || {}),
    ...payload,
  }

  dispatch(
    updatePropsAction({
      ...props,
      addressVO,
    })
  )
}

/**
 * 清除现有服务产品
 * @returns
 */
export const clearProvider = () => (dispatch, getState, form: FormInstance) => {
  dispatch(
    update({
      list: [],
      type: ENUM_TYPES.NORMAL,
      props: {},
      previousCodFee: undefined,
      previousShipmentServiceType: undefined,
    })
  )

  dispatch(clearSelectedProvider(form))
}

/**
 * 清除现有服务产品和列表
 * @returns
 */
export const clearSelectedProvider = (inputForm?: FormInstance) => (dispatch, getState, form: FormInstance) => {
  dispatch(
    update({
      provider: null,
      providerDateOptions: [],
      providerTimeOptions: [],
      porviderd: [],
      providerDeliveryDateOptions: [],
      providerDeliveryTimeOptions: [],
      providerPaymentOptions: [],
      tab: 0,
      filterArr: [],
    })
  )

  /**
   * 优先使用外面的
   */
  ;(inputForm || form)?.setFieldsValue({
    provider_payment_type: undefined,
    provider_cod_amount: undefined,

    /**
     *  发货方式
     */
    provider_pickup_type: undefined,

    /**
     * 预约揽收日期
     */
    provider_pickup_date: undefined,

    /**
     * 预约揽收时间
     */
    provider_pickup_time_slots: undefined,

    /**
     * 支付方式
     */
    provider_pay_type: undefined,

    /**
     * 月结账号
     */
    provider_monthly_account: undefined,

    /**
     * 月结子账号
     */
    provider_channel_seller_id: undefined,

    /**
     * 证件类型
     */
    provider_consignee_id_type: undefined,

    /**
     * 证件号
     */
    provider_consignee_id_num: undefined,

    /**
     * 税号类型
     */
    provider_tax_type: undefined,

    /**
     * 税号
     */
    provider_tax_no: undefined,

    /**
     * EORI号码
     */
    provider_custom_id_no: undefined,

    /**
     * 收件人姓名
     */
    provider_consignee_name: undefined,

    provider_custom_freight: undefined,
    provider_custom_freight_currency_code: undefined,
  })
}

/**
 * 是否需要清除服务产品
 * @returns
 */
export const shouldClearProvider = (params?: { shouldShowError: boolean }) => async (dispatch, getState: AppGetState, form: FormInstance) => {
  const { shouldShowError } = params || { shouldShowError: true }

  const state = getState()
  const { provider, props, type } = state.serviceProvider

  const list: ExpressListData[] = await dispatch(
    fetchProviderList({
      setStore: false,
      orderCodCurrencyCode: props?.codCurrencyCode,
    })
  )

  if (!list.length) {
    shouldShowError &&
      Toast.show({
        content: i18n.t('order.create.noMatch'),
        icon: 'error',
      })
  }

  /** 没有选中 provider 就不需要校验 */
  if (!provider) {
    /**
     * if there is in the create stated page and there is a list and without any selected provider
     * automated open the popup
     */
    if (type === 'create' && list?.length) {
      dispatch(fetchProviderList({ orderCodCurrencyCode: props?.codCurrencyCode }))
      dispatch(
        update({
          popupVisible: true,
        })
      )
    }
    return
  }

  const sortedList = sortProviderList(list, {
    filter: true,
  })

  const updateProvider = sortedList.find((item) => item?.expressTypeCode === provider?.expressTypeCode)

  // 是否需要清除选中的 provider
  if (provider?.expressTypeCode && !updateProvider) {
    dispatch(clearSelectedProvider(form))
    // 如果不清除就更新一下服务产品
  } else {
    // 更新服务产品
    dispatch(
      update({
        provider: {
          ...provider,
          ...updateProvider,
        },
      })
    )
    // 更新支付方式选项
    dispatch(handlePayTypeOptions())

    //  update extra config
    const formValues = form.getFieldsValue()

    const configData = await dispatch(
      handleExtraConfig({
        formValues,
        provider,
      })
    )

    dispatch(
      update({
        providerExtraConfig: configData.extraConfig,
        providerDateOptions: configData?.dateData?.shipmentPickupDateOptions,
        providerTimeOptions: configData?.dateData?.shipmentPickupTimeRangOptions,
        supportStationPickUp: configData?.supportStationPickUp,
      })
    )

    const updateFormValues = {
      provider_pickup_type: configData?.dateData.provider_pickup_type,
      provider_pickup_date: configData?.dateData.provider_pickup_date,
      provider_pickup_time_slots: configData?.dateData?.provider_pickup_time_slots,
      provider_tax_type: configData?.defaultTaxType,
    }

    form.setFieldsValue(updateFormValues)

    // 此处应该调用创单时将商品价格和数量计算得出初始COD金额逻辑（运费试算跳转创单，有初始服务产品，如果不调用的话，就不会走这段逻辑，所以此处需调用）
    await dispatch(calculateGoodsPrice(form, updateProvider))

    /** recalculate the custom freight */
    await dispatch(handleCalculateCustomFreight(form))

    await dispatch(handleCalculateFreightAndFeeItems(form))
  }
}

/** 支付方式选项
 * 是否只出现到付现结option(依赖服务产品币种)
 * 用户币种钱包没有此服务产品运费币种，只支持【到付现结】2(因为到付现结是线下给运费的，不扣钱包钱)
 * 算不出运费，也只支持【到付现结】
 */
export const handlePayTypeOptions = (): AppThunk => async (dispatch, getState) => {
  const {
    auth: { walletList, userInfo },
    serviceProvider,
  } = getState()
  const { provider, type } = serviceProvider

  if (provider) {
    const isPrepaid = userInfo.isPrepaid
    const { freightCurrencyCode, saasFlag, estimatedCost = 0, companyCode, settlementModes } = provider
    const userCurrencyCodeList = walletList.map((wallet) => wallet.currencyCode)
    const freightCurrencyCodeNoInUserCurrencyCodeList = freightCurrencyCode && !userCurrencyCodeList.includes(freightCurrencyCode)
    const { options } = handlePayment({
      companyCode,
      settlementModes,
      estimatedCost,
      isPrepaid,
      saasFlag,
      type,
      freightCurrencyCodeNoInUserCurrencyCodeList,
    })

    dispatch(update({ providerPaymentOptions: options }))
  }
}

export const updateShippingInfo = () => async (dispatch, getState: AppGetState) => {
  const state = getState()
  const { auth, serviceProvider } = state
  const { countryCode: senderCountryCode } = auth.userInfo ?? {}
  const companyCode = serviceProvider.provider?.companyCode

  const isKerryHk2TW = (() => {
    const { addressVO } = state.serviceProvider.props ?? {}
    if (addressVO && senderCountryCode && addressVO.arrival_countryCode) {
      return isKerryHk2TWFn(companyCode, senderCountryCode, addressVO.arrival_countryCode)
    } else {
      logger('发货国家或收货国家没有初始化')
      return false
    }
  })()

  dispatch(
    update({
      isKerryHk2TW,
    })
  )
}

/**
 *
 * @param inputForm
 * @returns
 */
export const handleCalculateCustomFreight =
  (inputForm?: FormInstance, sellerCodAmount?: number): AppThunk =>
  async (dispatch, getState: AppGetState, form: FormInstance) => {
    const state = getState()
    const { props, type, provider } = state.serviceProvider

    const finalForm = inputForm || form

    const values = finalForm?.getFieldsValue()

    const isCod = values.provider_payment_type === PaymentTypeEnum.cod

    // 运单编辑不执行自定义运费计算
    if (type === 'logistics' || !isCod) {
      return
    }

    const { orderInfo: originOrderInfo, supportedAddressTypes } = props

    const isHome = supportedAddressTypes?.[0] === 'HOME_STREET'

    const finalCodCurrencyCode = isCodCurrencyCodeSpecified(provider?.codCurrencyType) ? provider?.targetCodCurrencyCode : props.codCurrencyCode

    const shouldRecovery =
      originOrderInfo?.isECommerce &&
      originOrderInfo?.salesOrderAmount &&
      originOrderInfo?.salesOrderFreight &&
      props?.deductedFreight &&
      // 原订单币种是否与现有cod币种一致
      originOrderInfo?.salesOrderCurrencyCode === finalCodCurrencyCode

    // 如果要计算扣减，则传入订单原始orderAmount给后端计算扣减
    const finalCodFee = shouldRecovery ? originOrderInfo?.salesOrderAmount : times(Number(sellerCodAmount || values['provider_cod_amount']), 100)

    const address = {
      ...formatSenderAddressToService({ ...values }),
      ...formatRecipientAddressToService({ ...values }),
    }

    const feeConvertInfo = {
      currencyCode: values['parcel_currency_code'],
      // 优先使用传入的codAmount(某些情况下，cod输入框还没展示，form获取不到值)
      sellerCodAmount: finalCodFee,
      sellerCodCurrencyCode: finalCodCurrencyCode,
    }
    try {
      const res = await postFeeConvert({
        expressTypeCode: provider.expressTypeCode,
        width: values?.parcel_width,
        height: values?.parcel_height,
        length: values?.parcel_length,
        weight: values?.parcel_weight,
        address,
        feeConvertInfo,
        needSellerCustomFreight: true,
        isECommerce: originOrderInfo?.isECommerce,
        // 是否扣减运费，记录是否修改过，修改会置空,修改过cod,就不需要进行原始运费扣减
        salesOrderFreight: props?.deductedFreight ? originOrderInfo?.salesOrderFreight : 0,
        salesOrderCurrencyCode: props?.deductedFreight ? originOrderInfo?.salesOrderCurrencyCode : '',
        partitionInfo: {
          pickupType: finalForm?.getFieldValue('provider_pickup_type'),
          deliveryType: isHome ? DELIVER_VALUE_MAP.HOME : DELIVER_VALUE_MAP.STORE,
        },
      })
      if (res.bizCode === 'SUCCESS') {
        const { applySellerCustomFreight, sellerCustomFreightErrorCode, sellerCustomFreight, sellerCustomFreightCurrencyCode, deductedFreight } = res?.data
        form.setFieldsValue({
          provider_custom_freight_currency_code: sellerCustomFreightCurrencyCode,
          provider_custom_freight: divide(sellerCustomFreight || 0, 100),
        })

        /**
         * 设置自定义运费错误
         */
        const setCustomFreightError = async (error: string) => {
          // price error with the OneShip custom freight calculation
          type IFreightErrorType = `${CUSTOM_FREIGHT_REASON_ENUM}`
          await dispatch(
            setProps({
              osFreightErrorCode: error as IFreightErrorType,
            })
          )
        }

        // 移动端不管接口返回是否应用自定义运费，只要自定义运费币种与COD币种不一致，则提示不能使用自定义运费
        if (sellerCustomFreightCurrencyCode && finalCodCurrencyCode !== sellerCustomFreightCurrencyCode) {
          setCustomFreightError(CUSTOM_FREIGHT_REASON_ENUM.CAN_NOT_USE)
        } else {
          if (applySellerCustomFreight) {
            // 应用自定义运费
            await dispatch(
              setProps({
                osFreightErrorCode: undefined,
              })
            )
          } else if (sellerCustomFreightErrorCode) {
            // 应用自定义运费失败
            setCustomFreightError(sellerCustomFreightErrorCode)
          }
        }

        // 处理扣减逻辑
        // 如果没有应用自定义运费，又有原始扣减值，表单中的COD要需要恢复减去运费的值
        if (shouldRecovery) {
          if (!deductedFreight) {
            form.setFieldsValue({
              provider_cod_amount: divide(finalCodFee, 100),
            })
          } else {
            form.setFieldsValue({
              provider_cod_amount: divide(finalCodFee - deductedFreight, 100),
            })
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

const clearCustomFreight =
  (inputForm: FormInstance): AppThunk =>
  async (dispatch) => {
    await dispatch(
      setProps({
        osFreightErrorCode: undefined,
      })
    )

    inputForm.setFieldsValue({
      provider_custom_freight_currency_code: undefined,
      provider_custom_freight: undefined,
    })
  }

export const calculateGoodsPrice = (form: FormInstance, provider: ExpressListData) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { serviceProvider } = getState()
  const { parcel_commodities, parcel_currency_code, provider_payment_type } = form.getFieldsValue()

  const { type, previousCodFee, props } = serviceProvider
  const { codCurrencyCode } = props || {}

  const finalCodCurrencyCode = isCodCurrencyCodeSpecified(provider.codCurrencyType) ? provider?.targetCodCurrencyCode : codCurrencyCode
  if (type === 'create' && !previousCodFee) {
    const total =
      parcel_commodities?.reduce((acc, next) => {
        return (acc += times(next.parcel_goods_price, next.parcel_goods_quantity || 1))
      }, 0) || ''

    if (typeof total !== 'number') {
      return
    } else {
      await dispatch(
        setProps({
          // 创建订单，订单原始COD金额和币种赋值为商品价格及数量计算出的值
          apiCodAmount: times(total, 100),
          apiCodCurrencyCode: parcel_currency_code,
        })
      )
    }
    if (finalCodCurrencyCode === parcel_currency_code) {
      await dispatch(
        update({
          previousCodFee: total,
        })
      )
      // 如果默认为代收货款，将计算的金额直接赋值表单代收货款
      if (provider_payment_type === PaymentTypeEnum.cod) {
        form.setFieldsValue({
          provider_cod_amount: total,
        })
      }
    }
  }
}

export default configSlice.reducer
